import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { getHtmlText } from '../../lib/get-html-text';
import {
  fullySelectSelectedLink,
  getSelectedLink,
  getSelectedRange,
  getSelectedText,
  getSelectedTextNodeMeta,
  getSelectedTextRect,
} from '../../lib/get-selected-text-rect';
import { sleep } from '../../lib/sleep';
import { ToastService } from '../../services/toast.service';
import { WysiwygService } from '../services/wysiwyg.service';

@Directive({
  selector: '[wysiwyg]',
  standalone: true,
})
export class WysiwygDirective implements OnChanges, OnInit {
  private elRef = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);
  private toast = inject(ToastService);
  private _sanitizer = inject(DomSanitizer);
  private wysiwygService = inject(WysiwygService);

  @HostListener('keypress', ['$event'])
  onKeyPress(e: KeyboardEvent) {
    const length = getHtmlText(this.getValue()).length;
    if (this.maxLength && length >= this.maxLength) {
      this.toast.add('Max length exeeded', 'error');
      e.preventDefault();
      return false;
    }
  }

  @HostListener('input', ['$event'])
  async onInput() {
    await sleep(0);
    this.save();
  }

  @HostListener('paste', ['$event'])
  async onPaste(e: ClipboardEvent) {
    const textToPaste = this.sanitize(e.clipboardData.getData('text'));
    const currentLength = getHtmlText(this.getValue()).length;
    const range = getSelectedRange(this.elRef.nativeElement);
    const selectedContent = range?.cloneContents()?.textContent;
    const selectedContentLength = selectedContent?.length || 0;

    e.preventDefault();
    if (this.maxLength && currentLength + textToPaste.length - selectedContentLength > this.maxLength) {
      this.toast.add('Max length exeeded', 'error');
    }
    const charactersLeft = (this.maxLength || Infinity) - (currentLength - selectedContentLength);
    const newTextToPaste = textToPaste.slice(0, charactersLeft);
    document.execCommand('insertHTML', false, newTextToPaste);

    this.save();
  }

  @HostBinding('class') elementClass: string;
  @Input('wysiwyg') model: string;
  @Input() allowHtml: boolean;
  @Input() allowFontChanges: boolean;
  @Input() allowHugeFontSize: boolean;
  @Input() allowAlignment: boolean;
  // @Input('toolbar') toolbar: ToolbarComponent;
  @Input() maxLength?: number;
  @Input() fontFamily?: string;
  @Input() defaultFontFamily?: string;
  @Input() alignment?: 'left' | 'center' | 'right';
  @Input() fontSize?: 'small' | 'medium' | 'large' | 'huge';
  @Input() textColor?: string;
  @Input() allowTextColorReset?: boolean;
  @Input() darkBackground?: boolean;
  @Input() wysiwygHoverIdentifier?: string;
  @Output() wysiwygChange = new EventEmitter();
  @Output() onFontChange = new EventEmitter();
  @Output() onFontSizeChange = new EventEmitter();
  @Output() onAlignmentChange = new EventEmitter();
  @Output() onTextColorChange = new EventEmitter();
  storedRange: Range;
  toolbarOpen = false;

  @HostListener('document:selectionchange', ['$event'])
  selectionChange() {
    this.setToolbar();
  }

  @HostListener('window:scroll', ['$event'])
  scroll() {
    this.setToolbar();
  }

  /**
   * By updating this property on keyup, and checking against it during
   * ngOnChanges, we can rule out change events fired by our own onKeyup.
   * Ideally we would not have to check against the whole string on every
   * change, could possibly store a flag during onKeyup and test against that
   * flag in ngOnChanges, but implementation details of Angular change detection
   * cycle might make this not work in some edge cases?
   */
  private lastViewModel: string;

  ngOnInit(): void {
    this.elementClass = 'wysiwyg-content no-style';
    if (!this.allowHtml) return;
    this.elementClass = 'wysiwyg-content';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && changes['model'].currentValue !== this.lastViewModel) {
      this.lastViewModel = this.model.trim();
      this.refreshView();
    }
  }

  getToolbarPosition(): DOMRect {
    const textRect = getSelectedTextRect(this.elRef.nativeElement);
    if (!textRect) return;

    if (ScreenSizes.screenSize() === 'mobile') {
      return <DOMRect>{
        left: 0,
        width: window.innerWidth,
        top: textRect.bottom + 20,
      };
    }

    return {
      ...textRect,
      top: Math.max(textRect.top, 120),
    };
  }

  setToolbar() {
    if (!this.allowHtml && !this.allowFontChanges && !this.allowAlignment) return;
    if (!this.contenteditable) return;

    const text = getSelectedText(this.elRef.nativeElement);
    const aEl = getSelectedLink(this.elRef.nativeElement);
    const href = aEl ? aEl.getAttribute('href') : '';

    if (!text && !href) {
      if (!this.toolbarOpen) return;
      return this.wysiwygService.hideToolbar();
    }

    this.toolbarOpen = true;
    this.wysiwygService.showToolbar({
      allowFontChanges: this.allowFontChanges,
      allowHugeFontSize: this.allowHugeFontSize,
      allowAlignment: this.allowAlignment,
      allowHtml: this.allowHtml,
      disableHtml: !text,
      alignment: this.alignment || 'center',
      urlMode: href && !text?.length,
      href: href,
      position: this.getToolbarPosition(),
      fontFamily: this.fontFamily,
      defaultFontFamily: this.defaultFontFamily || 'AkkuratLL',
      fontSize: this.fontSize,
      textColor: this.textColor,
      allowTextColorReset: this.allowTextColorReset,
      darkBackground: this.darkBackground,
      meta: getSelectedTextNodeMeta(this.elRef.nativeElement),
      onLink: this.addLink.bind(this),
      onAction: this.onAction.bind(this),
      restoreSelection: this.restoreSelection.bind(this),
      onChangeFont: this.onChangeFont.bind(this),
      onFontSizeChanged: this.onFontSizeChanged.bind(this),
      onAlignmentChanged: this.onAlignmentChanged.bind(this),
      onTextColorChanged: this.onTextColorChanged.bind(this),
      wysiwygHoverIdentifier: this.wysiwygHoverIdentifier,
      onClose: () => (this.toolbarOpen = false),
    });
    const range = getSelectedRange(this.elRef.nativeElement);
    if (range) this.storedRange = range;
  }

  unicodeToChar(text: string) {
    return text.replace(/&#[0-9]{2};/gi, function (match) {
      return String.fromCharCode(parseInt(match.replace(/\D/g, '')));
    });
  }

  private sanitize(unsafeHtml: string) {
    const html = this._sanitizer.sanitize(SecurityContext.HTML, unsafeHtml);
    if (html === '<br>') return '';
    return this.unicodeToChar(html);
  }

  private getValue() {
    return this.sanitize(this.elRef.nativeElement.innerHTML.trim());
  }

  onAction() {
    this.save();
  }

  onChangeFont(fontFamily: string) {
    this.fontFamily = fontFamily;
    this.restoreSelection();
    this.onFontChange.emit(fontFamily);
    this.onAction();
  }

  onFontSizeChanged(size: 'small' | 'medium' | 'large') {
    this.onFontSizeChange.emit(size);
    this.onAction();
  }

  async onAlignmentChanged(alignment: 'left' | 'center' | 'right') {
    this.onAlignmentChange.emit(alignment);
    this.alignment = alignment;
    this.onAction();
    await sleep(0);
    this.setToolbar();
  }

  onTextColorChanged(color: string) {
    this.onTextColorChange.emit(color);
    this.onAction();
  }

  restoreSelection() {
    if (!this.storedRange) return;
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(this.storedRange);
  }

  addLink(href: string) {
    this.restoreSelection();
    const aEl = getSelectedLink(this.elRef.nativeElement);
    if (!href || aEl) fullySelectSelectedLink(this.elRef.nativeElement);

    if (href) {
      // TODO: make external link
      document.execCommand('createLink', false, href);
    } else {
      document.execCommand('unlink', false, null);
    }
  }

  private refreshView() {
    this.elRef.nativeElement.innerHTML = this.model;
  }

  private save() {
    let value = this.getValue();
    const text = getHtmlText(value);
    if (this.maxLength && text.length > this.maxLength) {
      value = text.slice(0, this.maxLength);
      this.toast.add('Max length exeeded', 'error');
      this.model = value;
      this.refreshView();
    }
    this.lastViewModel = value;
    this.wysiwygChange.emit(value);
  }

  private get contenteditable() {
    const contenteditable = this.elRef.nativeElement.getAttribute('contenteditable');
    return contenteditable === 'true' || contenteditable === '';
  }
}
